import React, { useState, useEffect } from 'react';
import { Box, Typography, Checkbox, Button, TextField, Chip } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import { CheckCircle, Event, Schedule, LocationOn } from '@material-ui/icons';
import { ActivitiesAnalyseResponse, ActivitySchedule, ClarificationAnswer, WeekDay } from './types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    resultsContainer: {
      maxHeight: '450px',
      overflow: 'auto',
    },
    summaryBox: {
      backgroundColor: theme.palette.grey[50],
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(3),
    },
    resultSection: {
      marginBottom: theme.spacing(3),
    },
    resultSectionTitle: {
      fontWeight: 600,
      marginBottom: theme.spacing(1.5),
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.text.primary,
    },
    sectionIcon: {
      color: theme.palette.text.secondary,
      fontSize: 22,
      marginRight: theme.spacing(1.5),
    },
    activityCard: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
      backgroundColor: theme.palette.background.paper,
      cursor: 'pointer',
    },
    activityCardHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: theme.spacing(1),
    },
    activityCheckbox: {
      padding: 0,
      marginRight: theme.spacing(1.5),
    },
    activityIcon: {
      color: theme.palette.text.secondary,
      fontSize: 20,
      marginRight: theme.spacing(1),
    },
    activityContent: {
      flex: 1,
    },
    activityName: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      fontSize: '0.875rem',
    },
    activityDescription: {
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
    },
    activityMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      marginLeft: theme.spacing(6.5),
      marginBottom: theme.spacing(1),
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.text.secondary,
      fontSize: '0.8rem',
      marginRight: theme.spacing(2),
    },
    metaIcon: {
      fontSize: 16,
      color: theme.palette.text.disabled,
      marginRight: theme.spacing(0.5),
    },
    sessionsContainer: {
      marginLeft: theme.spacing(6.5),
      marginTop: theme.spacing(1),
    },
    sessionItem: {
      display: 'inline-block',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(0.5, 1),
      marginRight: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
    },
    activityReason: {
      color: theme.palette.text.disabled,
      fontSize: '0.75rem',
      marginLeft: theme.spacing(6.5),
      fontStyle: 'italic',
      marginTop: theme.spacing(1),
    },
    programSelector: {
      marginLeft: theme.spacing(6.5),
      marginTop: theme.spacing(1),
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    programLabel: {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(1),
    },
    programButton: {
      fontSize: '0.7rem',
      padding: theme.spacing(0.25, 1),
      marginRight: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
      minWidth: 'auto',
      textTransform: 'none',
    },
    questionCard: {
      border: `1px solid ${theme.palette.warning.light}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(1),
      backgroundColor: 'rgba(255, 152, 0, 0.04)',
    },
    questionCardSkipped: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(1),
      backgroundColor: theme.palette.grey[50],
      opacity: 0.6,
    },
    questionHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
    },
    questionText: {
      fontWeight: 500,
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
      flex: 1,
    },
    questionContext: {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.5),
    },
    questionAnswer: {
      marginTop: theme.spacing(1),
    },
    skipButton: {
      textTransform: 'none',
      fontSize: '0.75rem',
      marginLeft: theme.spacing(1),
      flexShrink: 0,
    },
    skippedChip: {
      fontSize: '0.7rem',
      height: 22,
      marginLeft: theme.spacing(1),
    },
  })
);

const DAY_NAMES: Record<WeekDay, string> = {
  MO: 'Mon',
  TU: 'Tue',
  WE: 'Wed',
  TH: 'Thu',
  FR: 'Fri',
  SA: 'Sat',
  SU: 'Sun',
};

function formatSchedule(schedule: ActivitySchedule): string {
  switch (schedule.type) {
    case 'single':
      return 'One-time';
    case 'daily':
      return schedule.dayInterval === 1 ? 'Daily' : `Every ${schedule.dayInterval} days`;
    case 'weekly': {
      const days = schedule.days?.map((d) => DAY_NAMES[d]).join(', ') || '';
      const interval = schedule.weekInterval === 1 ? 'Weekly' : `Every ${schedule.weekInterval} weeks`;
      return days ? `${interval} (${days})` : interval;
    }
    case 'monthlyByDate':
      return `Monthly on day ${schedule.monthDay}`;
    case 'monthlyByNthDay': {
      const nth = ['1st', '2nd', '3rd', '4th', 'Last'][Math.min((schedule.nthWeek || 1) - 1, 4)];
      const day = schedule.dayOfWeek ? DAY_NAMES[schedule.dayOfWeek] : '';
      return `${nth} ${day} of month`;
    }
    case 'yearly':
      return 'Yearly';
    default:
      return 'Custom';
  }
}

function formatSessionTime(startTime?: string, endTime?: string): string {
  if (!startTime && !endTime) {
    return 'No set time';
  }
  if (startTime && endTime) {
    return `${startTime} - ${endTime}`;
  }
  if (startTime) {
    return `From ${startTime}`;
  }
  return `Until ${endTime}`;
}

interface Program {
  programId: string;
  name: string;
}

interface ActivitiesResultsViewProps {
  results: ActivitiesAnalyseResponse;
  selectedActivities: Set<string>;
  onToggleActivity: (activityName: string) => void;
  programs: Program[];
  activityProgramMap: Map<string, string>;
  onSetActivityProgram: (activityName: string, programId: string) => void;
  onReanalyse?: (clarificationAnswers: ClarificationAnswer[]) => void;
  onQuestionsResolved?: (resolved: boolean) => void;
}

export function ActivitiesResultsView({
  results,
  selectedActivities,
  onToggleActivity,
  programs,
  activityProgramMap,
  onSetActivityProgram,
  onReanalyse,
  onQuestionsResolved,
}: ActivitiesResultsViewProps) {
  const classes = useStyles();
  const showProgramSelector = programs.length > 1;
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [skipped, setSkipped] = useState<Set<string>>(new Set());

  const questions = results.questions || [];
  const hasQuestions = questions.length > 0;

  const allResolved = hasQuestions
    ? questions.every((q) => skipped.has(q.question) || (answers[q.question] || '').trim() !== '')
    : true;

  const hasAnyAnswer = Object.values(answers).some((a) => a.trim() !== '');

  useEffect(() => {
    if (onQuestionsResolved) {
      onQuestionsResolved(allResolved);
    }
  }, [allResolved, onQuestionsResolved]);

  const handleReanalyse = () => {
    if (!onReanalyse) return;
    const clarifications: ClarificationAnswer[] = [];
    questions.forEach((q) => {
      const answer = answers[q.question]?.trim();
      if (answer) {
        clarifications.push({ question: q.question, answer });
      }
    });
    if (clarifications.length > 0) {
      onReanalyse(clarifications);
    }
  };

  const handleSkip = (questionText: string) => {
    setSkipped((prev) => {
      const next = new Set(prev);
      if (next.has(questionText)) {
        next.delete(questionText);
      } else {
        next.add(questionText);
      }
      return next;
    });
  };

  return (
    <Box className={classes.resultsContainer}>
      {/* Summary */}
      <Box className={classes.summaryBox}>
        <Box display='flex' alignItems='center' mb={1}>
          <CheckCircle style={{ color: '#4caf50', marginRight: 12 }} />
          <Typography variant='subtitle1' style={{ fontWeight: 600 }}>
            Analysis Complete
          </Typography>
        </Box>
        <Typography variant='body2' color='textSecondary'>
          {results.analysis.summary}
        </Typography>
      </Box>

      {/* Questions - shown at top when present */}
      {hasQuestions && (
        <Box className={classes.resultSection}>
          <Typography className={classes.resultSectionTitle}>Questions to Clarify</Typography>
          <Typography variant='body2' color='textSecondary' style={{ marginBottom: 12 }}>
            Answer these questions to improve the results, or skip to proceed with the current analysis.
          </Typography>
          {questions.map((question) => {
            const isSkipped = skipped.has(question.question);
            return (
              <Box
                key={`${question.activity}-${question.field}-${question.question}`}
                className={isSkipped ? classes.questionCardSkipped : classes.questionCard}
              >
                <Box className={classes.questionHeader}>
                  <Box flex={1}>
                    <Typography className={classes.questionText}>{question.question}</Typography>
                    {question.context && (
                      <Typography className={classes.questionContext}>{question.context}</Typography>
                    )}
                  </Box>
                  {isSkipped ? (
                    <Chip
                      label='Skipped'
                      size='small'
                      className={classes.skippedChip}
                      onClick={() => handleSkip(question.question)}
                      onDelete={() => handleSkip(question.question)}
                    />
                  ) : (
                    <Button size='small' className={classes.skipButton} onClick={() => handleSkip(question.question)}>
                      Skip
                    </Button>
                  )}
                </Box>
                {!isSkipped && (
                  <TextField
                    fullWidth
                    size='small'
                    variant='outlined'
                    placeholder='Type your answer...'
                    value={answers[question.question] || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setAnswers((prev) => ({ ...prev, [question.question]: val }));
                    }}
                    className={classes.questionAnswer}
                  />
                )}
              </Box>
            );
          })}
          {onReanalyse && hasAnyAnswer && (
            <Button
              variant='outlined'
              color='primary'
              size='small'
              onClick={handleReanalyse}
              style={{ textTransform: 'none', marginTop: 8 }}
            >
              Re-analyse with answers
            </Button>
          )}
        </Box>
      )}

      {/* Activities */}
      {results.activities && results.activities.length > 0 && (
        <Box className={classes.resultSection}>
          <Typography className={classes.resultSectionTitle}>
            <Event className={classes.sectionIcon} />
            Activities to Create ({selectedActivities.size} of {results.activities.length} selected)
          </Typography>
          {results.activities.map((activity) => (
            <Box
              key={activity.name}
              className={classes.activityCard}
              onClick={() => onToggleActivity(activity.name)}
              style={{ opacity: selectedActivities.has(activity.name) ? 1 : 0.6 }}
            >
              <Box className={classes.activityCardHeader}>
                <Checkbox
                  checked={selectedActivities.has(activity.name)}
                  color='primary'
                  size='small'
                  className={classes.activityCheckbox}
                />
                <Event className={classes.activityIcon} />
                <Box className={classes.activityContent}>
                  <Typography className={classes.activityName}>{activity.name}</Typography>
                  {activity.description && (
                    <Typography className={classes.activityDescription}>{activity.description}</Typography>
                  )}
                </Box>
              </Box>

              <Box className={classes.activityMeta}>
                {activity.location && (
                  <Box className={classes.metaItem}>
                    <LocationOn className={classes.metaIcon} />
                    {activity.location}
                  </Box>
                )}
                {activity.schedule && (
                  <Box className={classes.metaItem}>
                    <Schedule className={classes.metaIcon} />
                    {formatSchedule(activity.schedule)}
                  </Box>
                )}
              </Box>

              {activity.sessions && activity.sessions.length > 0 && (
                <Box className={classes.sessionsContainer}>
                  {activity.sessions.map((session) => (
                    <Typography key={session.name} className={classes.sessionItem}>
                      {session.name} ({formatSessionTime(session.startTime, session.endTime)})
                    </Typography>
                  ))}
                </Box>
              )}

              {showProgramSelector && (
                <Box className={classes.programSelector}>
                  <Typography className={classes.programLabel}>Program:</Typography>
                  {programs.map((program) => {
                    const isSelected = activityProgramMap.get(activity.name) === program.programId;
                    return (
                      <Button
                        key={program.programId}
                        variant={isSelected ? 'contained' : 'outlined'}
                        size='small'
                        className={classes.programButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSetActivityProgram(activity.name, program.programId);
                        }}
                        style={{
                          backgroundColor: isSelected ? '#796fff' : undefined,
                          color: isSelected ? '#fff' : undefined,
                          borderColor: isSelected ? '#796fff' : undefined,
                        }}
                      >
                        {program.name}
                      </Button>
                    );
                  })}
                </Box>
              )}

              <Typography className={classes.activityReason}>Source: {activity.reason}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* No activities found */}
      {(!results.activities || results.activities.length === 0) && (
        <Box className={classes.resultSection}>
          <Typography variant='body2' color='textSecondary'>
            No activities were identified from the provided documents. You can create activities manually later.
          </Typography>
        </Box>
      )}

      {/* Recommendations */}
      {results.recommendations && results.recommendations.length > 0 && (
        <Box className={classes.resultSection}>
          <Typography className={classes.resultSectionTitle}>Recommendations</Typography>
          <Box component='ul' style={{ margin: 0, paddingLeft: 20 }}>
            {results.recommendations.map((rec) => (
              <li key={rec}>
                <Typography variant='body2' color='textSecondary'>
                  {rec}
                </Typography>
              </li>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// Helper to get initial selections from response
export function getInitialActivitySelections(response: ActivitiesAnalyseResponse) {
  return {
    activities: new Set(response.activities.map((a) => a.name)),
  };
}

// Helper to build apply summary
export function getActivitiesApplySummary(selectedActivities: Set<string>): string {
  if (selectedActivities.size === 0) {
    return 'No activities selected';
  }
  return `${selectedActivities.size} activit${selectedActivities.size !== 1 ? 'ies' : 'y'} will be created`;
}
